import { uniq } from "@-/com.npmjs.array-utils-ts";
import { Pressable, ScrollView, View, type ViewProps } from "@-/com.npmjs.react-native";
import { LongPressable } from "@-/dev.improve.haniverse.components.ui.long-pressable";
import { SurfaceHeader, SurfaceContent } from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import type { ReferenceGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import {
  graphemeFontWeightStyle,
  useFontStyleForGrapheme,
  useGraphemeDisplayText,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.font";
import { PinyinPronunciation } from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-pronunciation";
import { MorphemeInfoPanelDictionaryCompositionSection } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.dictionary.__sections.overview._sections.composition-section";
import { useCardFrequencyRank } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.rank";
import { useMorphemeInfoPanelDictionaryGradeLabel } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.dictionary.__sections.overview._sections.grade-badge";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { GraphemeDictionaryUsageBadge } from "#grapheme-dictionary-usage-badge";
import {
  MorphemeInfoPanelDictionarySimilarSection,
  useMorphemeInfoPanelDictionarySimilarSectionGate,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content.views.dictionary.__sections.overview._sections.similar-section";
import { getTaiwanUsageMap } from "@-/dev.improve.haniverse.data.taiwan-usage-map";
import { useConvertedCharacter } from "@-/dev.improve.haniverse.lib.hanzi-script";
import { copyToClipboard } from "@-/dev.improve.haniverse.lib.clipboard";
import { useMemo } from "react";
import { morphemeSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelection } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";

export function GraphemeDictionarySurfaceHeader({
  graphemeCard,
  onLayout,
}: {
  graphemeCard: ReferenceGraphemeMorphemeSlice;
  onLayout?: ViewProps["onLayout"];
}) {
  const readings = usePrevalentPinyin(graphemeCard.grapheme, graphemeCard.pinyinList);
  const glyph = useGetGraphemeGlyph()(graphemeCard.grapheme);
  const displayText = useGraphemeDisplayText(glyph.display);
  const fontStyle = useFontStyleForGrapheme(glyph.display);
  const gradeLabel = useMorphemeInfoPanelDictionaryGradeLabel(graphemeCard);
  const rank = useCardFrequencyRank(graphemeCard);
  const { hanziScript } = useActorUser();
  const selectionItems = useMemo(
    () => [morphemeSelectionItem(graphemeCard.grapheme)],
    [graphemeCard.grapheme],
  );
  const { consumePress, isSelected, onLongPress } = useAppSelection(selectionItems);
  const [expanded, setExpanded] = useRouteBodyState("grapheme-surface-definitions-expanded", false);
  const definitions = uniq(
    graphemeCard.definitionList.flatMap(({ entries }) =>
      entries.flatMap((entry) =>
        entry
          .split(";")
          .map((definition) => definition.trim())
          .filter(Boolean),
      ),
    ),
  );
  const joinedDefinitions = definitions.join("; ");
  const expandable = definitions.length > 1 || joinedDefinitions.length > 60;

  return (
    <SurfaceHeader className="px-0" onLayout={onLayout} testID="grapheme-dictionary-surface-header">
      <View className="flex-row items-start gap-x-2">
        <View className="w-28 shrink-0 items-center p-2">
          <LongPressable
            accessibilityLabel={`Copy ${glyph.display}`}
            accessibilityState={{ selected: isSelected }}
            className={isSelected ? "rounded-xs bg-primary/10" : undefined}
            onLongPress={onLongPress}
            onPress={() => {
              if (!consumePress()) {
                void copyToClipboard(glyph.display, { label: glyph.display });
              }
            }}
          >
            <Text
              className="text-center text-2xl leading-8 font-medium text-foreground"
              style={[
                fontStyle,
                graphemeFontWeightStyle(glyph.fontWeight),
                glyph.color === null ? undefined : { color: glyph.color },
              ]}
              variant="glyph"
            >
              {displayText}
            </Text>
          </LongPressable>
          <PinyinPronunciation
            card={{ ...graphemeCard, pinyinList: readings }}
            pinyinList={readings.map((reading) => toDiacriticPinyin(reading.toneNumbersPinyin))}
          />
        </View>
        <View className="min-w-0 flex-1 py-2 pr-2">
          <View className="flex-row items-start gap-x-1">
            <View className="min-w-0 flex-1">
              {definitions.length === 0 ? (
                <Text className="text-sm text-muted-foreground italic">
                  No dictionary definitions found yet.
                </Text>
              ) : expanded ? (
                <ScrollView className="max-h-36" nestedScrollEnabled>
                  {definitions.map((definition, index) => (
                    <Text className="text-sm leading-5 text-muted-foreground" key={index}>
                      • {definition}
                    </Text>
                  ))}
                </ScrollView>
              ) : (
                <Text className="text-sm leading-5 text-muted-foreground" numberOfLines={2}>
                  {joinedDefinitions}
                </Text>
              )}
            </View>
            {expandable ? (
              <Pressable
                accessibilityLabel={`${expanded ? "Show fewer" : "Show all"} definitions for ${graphemeCard.grapheme}`}
                accessibilityRole="button"
                accessibilityState={{ expanded }}
                className="p-1"
                hitSlop={6}
                onPress={() => setExpanded((value) => !value)}
              >
                <Icon
                  className="text-muted-foreground"
                  name={expanded ? "LuChevronUp" : "LuChevronDown"}
                  size={16}
                />
              </Pressable>
            ) : null}
          </View>
        </View>
        <View className="shrink-0 items-end gap-y-0.5 p-2">
          <Text className="text-xs text-muted-foreground">
            {rank === null ? "No Rank" : `#${rank}`}
          </Text>
          <Text className="text-xs text-muted-foreground">
            {gradeLabel ?? (hanziScript === "traditional" ? "Non-TBCL" : "Non-HSK")}
          </Text>
        </View>
      </View>
    </SurfaceHeader>
  );
}

export function GraphemeDictionarySurfaceBody({
  graphemeCard,
}: {
  graphemeCard: ReferenceGraphemeMorphemeSlice;
}) {
  const traditional = useConvertedCharacter(graphemeCard.grapheme, "simplified", "traditional");
  const taiwanUsageClassification = getTaiwanUsageMap()[traditional] ?? null;
  const similarGate = useMorphemeInfoPanelDictionarySimilarSectionGate();
  const gradeLabel = useMorphemeInfoPanelDictionaryGradeLabel(graphemeCard);

  return (
    <SurfaceContent className="gap-y-3 p-3" testID="grapheme-dictionary-surface-body">
      {gradeLabel === null ? (
        <GraphemeDictionaryUsageBadge
          card={graphemeCard}
          taiwanUsageClassification={taiwanUsageClassification}
        />
      ) : null}
      <MorphemeInfoPanelDictionaryCompositionSection
        visibleComponentList={graphemeCard.components}
      />
      {similarGate === null ? null : (
        <MorphemeInfoPanelDictionarySimilarSection gate={similarGate} />
      )}
    </SurfaceContent>
  );
}
